/**
 * Typed client for SnappyMail's JSON API.
 *
 * SnappyMail stays as the mail backend -- it does IMAP/SMTP, MIME parsing and
 * HTML sanitization, none of which is worth reimplementing. This module is the
 * ONLY place that knows how to talk to it. Nothing in the UI should call the API
 * directly, because that API is an internal RPC with no compatibility promise:
 * upstream renames things freely and we only control when we merge. Keeping it
 * behind one file makes an upstream change a one-file fix.
 *
 * Wire format, taken from dev/boot.js and dev/Remote/AbstractFetch.js:
 *
 *   POST /?/Json/&q[]=/0/
 *   Content-Type: application/json
 *   X-SM-Token: <csrf token from app data>
 *   {"Action": "Folders", ...params}
 *
 * Everything dynamic in SnappyMail is the bare "/" path plus a query string, and
 * everything static is a real path. That is what lets this SPA live at /app/
 * without colliding with any of it.
 */

/** SnappyMail's notification codes. Only the ones we act on. */
export const enum ApiError {
	InvalidToken = 101,
	AuthError = 102,
	ConnectionError = 104,
	/** Sent fine; only filing the copy in Sent failed. */
	CantSaveMessage = 301,
	CantSendMessage = 302,
	UnknownError = 999
}

/** Markers the Keystone two-step login sends back in `messageAdditional`. */
export const TOTP_REQUIRED = 'KEYSTONE_TOTP_REQUIRED';
export const TOTP_INVALID = 'KEYSTONE_TOTP_INVALID';

/**
 * The response envelope, from Actions/Response.php.
 *
 * Note `code`, not `ErrorCode`. Getting that name wrong is silent: the failure
 * looks like a success whose Result happens to be `false`, so a rejected login
 * -- including the 2FA challenge -- reads as "nothing happened".
 */
export interface ApiResponse<T> {
	Action: string;
	/** `false` on any failure; the real value otherwise. */
	Result: T | false;
	code?: number;
	message?: string;
	messageAdditional?: string;
}

/** Thrown for any non-success response, so callers can use try/catch throughout. */
export class ApiRequestError extends Error {
	constructor(
		readonly action: string,
		readonly code: number,
		readonly additional: string = ''
	) {
		super(`${action} failed (${code})${additional ? ': ' + additional : ''}`);
		this.name = 'ApiRequestError';
	}

	/** The password was right and this account needs its 2FA code. */
	get needsTotp(): boolean {
		return TOTP_REQUIRED === this.additional;
	}

	/** The code supplied was wrong; stay on the second step. */
	get badTotp(): boolean {
		return TOTP_INVALID === this.additional;
	}
}

/** Shape of the bits of app data we actually use. */
export interface AppData {
	Auth: boolean;
	Email?: string;
	System: {
		token: string;
		[key: string]: unknown;
	};
	[key: string]: unknown;
}

const JSON_MIME = 'application/json';

/**
 * The API lives at the site root, not under this app's base path -- SnappyMail
 * owns "/" plus a query string and we own /app/.
 */
const JSON_URL = '/?/Json/&q[]=/0/';
const APP_DATA_URL = '/?/AppData/0/0/';
const UPLOAD_URL = '/?/Upload/&q[]=/0/';

let csrfToken = '';

async function readJson<T>(response: Response, action: string): Promise<T> {
	if (!response.ok) {
		throw new ApiRequestError(action, response.status);
	}

	// SnappyMail answers with HTML when a request is rejected before routing --
	// a Sec-Fetch refusal, most often. Parsing that as JSON gives a useless
	// syntax error, so fail with something that names the real problem.
	const type = response.headers.get('Content-Type') ?? '';
	if (!type.startsWith(JSON_MIME)) {
		throw new ApiRequestError(action, response.status, `expected JSON, got "${type}"`);
	}

	return response.json() as Promise<T>;
}

/**
 * Fetches app data, which both establishes whether there is a session and
 * carries the CSRF token every later call needs. Call this first.
 */
export async function loadAppData(): Promise<AppData> {
	const response = await fetch(APP_DATA_URL, {
		mode: 'same-origin',
		cache: 'no-cache',
		credentials: 'same-origin',
		referrerPolicy: 'no-referrer',
		headers: { Accept: JSON_MIME }
	});

	const data = await readJson<AppData>(response, 'AppData');
	csrfToken = data?.System?.token ?? '';
	setAccountHash(String(data?.accountHash ?? ''));
	return data;
}

/**
 * Calls one JSON action.
 *
 * Credentials are same-origin because SnappyMail's cookies are SameSite=Strict.
 * That is also why this app has to be served from the same origin as the API --
 * from another host the browser would send no cookies at all and every call
 * would come back unauthenticated. See the rebuild plan in the vault.
 */
export async function call<T>(action: string, params: Record<string, unknown> = {}): Promise<T> {
	const headers: Record<string, string> = {
		Accept: JSON_MIME,
		'Content-Type': JSON_MIME
	};
	if (csrfToken) {
		headers['X-SM-Token'] = csrfToken;
	}

	const response = await fetch(JSON_URL, {
		method: 'POST',
		mode: 'same-origin',
		cache: 'no-cache',
		credentials: 'same-origin',
		referrerPolicy: 'no-referrer',
		headers,
		body: JSON.stringify({ Action: action, ...params })
	});

	const data = await readJson<ApiResponse<T>>(response, action);

	// Failures arrive as HTTP 200 with the detail in the body: Result is `false`
	// and `code` carries the reason. Every failing path in the API goes through
	// FalseResponse/ExceptionResponse, so `Result === false` is never a
	// meaningful success value.
	if (data.code || false === data.Result || undefined === data.Result) {
		throw new ApiRequestError(
			action,
			data.code || ApiError.UnknownError,
			data.messageAdditional ?? data.message ?? ''
		);
	}

	return data.Result as T;
}

/**
 * Signs in.
 *
 * `totp` is omitted on the first attempt on purpose: the server answers with
 * TOTP_REQUIRED only for accounts that actually have 2FA, which is what lets the
 * UI ask for a code as a second step and never show that field to anyone else.
 */
export async function login(email: string, password: string, totp?: string): Promise<AppData> {
	await call<unknown>('Login', {
		Email: email,
		Password: password,
		totp_code: totp ?? '',
		Language: '',
		signMe: 0
	});

	// The token is reissued with the session, so app data has to be re-read
	// rather than reused from before the login.
	return loadAppData();
}

/**
 * Calls a plugin's JSON action.
 *
 * Plugin actions are registered with a "Plugin" prefix (see rl.pluginRemoteRequest
 * in dev/Common/Plugins.js), so `KeystonePushSubscribe` is invoked as
 * `PluginKeystonePushSubscribe`. Getting this wrong yields a 404-shaped failure
 * that looks like the plugin is not installed.
 */
export async function callPlugin<T>(
	action: string,
	params: Record<string, unknown> = {}
): Promise<T> {
	return call<T>('Plugin' + action, params);
}

export async function logout(): Promise<void> {
	await call<unknown>('Logout');
	csrfToken = '';
}

/* -------------------------------------------------------------------- uploads */

export interface UploadedAttachment {
	name: string;
	tempName: string;
	mimeType: string;
	size: number;
}

/**
 * Uploads one file and returns the handle the send/save actions expect.
 *
 * multipart/form-data with the field named `uploader`, not a JSON call -- see
 * privateUpload() in ServiceActions.php. The Content-Type header is deliberately
 * NOT set: the browser has to add it itself so it can include the multipart
 * boundary, and setting it by hand produces a request PHP cannot parse.
 */
export async function uploadFile(file: File): Promise<UploadedAttachment> {
	const body = new FormData();
	body.set('uploader', file);
	if (csrfToken) {
		body.set('XToken', csrfToken);
	}

	const headers: Record<string, string> = { Accept: JSON_MIME };
	if (csrfToken) {
		headers['X-SM-Token'] = csrfToken;
	}

	const response = await fetch(UPLOAD_URL, {
		method: 'POST',
		mode: 'same-origin',
		cache: 'no-cache',
		credentials: 'same-origin',
		referrerPolicy: 'no-referrer',
		headers,
		body
	});

	const data = await readJson<ApiResponse<{ Attachment?: UploadedAttachment }>>(
		response,
		'Upload'
	);

	if (data.code || !data.Result || !data.Result.Attachment) {
		throw new ApiRequestError(
			'Upload',
			data.code || ApiError.UnknownError,
			data.messageAdditional ?? data.message ?? ''
		);
	}

	return data.Result.Attachment;
}

/* ------------------------------------------------------------------ downloads */

/**
 * SnappyMail's URL-safe base64 of a JSON value, matching `b64EncodeJSONSafe` in
 * dev/Common/Utils.js. The escape/encodeURIComponent dance is how a UTF-16
 * DOMString becomes UTF-8 bytes before btoa, which only accepts latin1 -- a
 * filename with an accent in it throws without this.
 */
function encodeKey(value: unknown): string {
	const utf8 = encodeURIComponent(JSON.stringify(value)).replace(
		/%([0-9A-F]{2})/g,
		(_match, hex: string) => String.fromCharCode(parseInt(hex, 16))
	);
	return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

let accountHash = '';

/** Raw endpoints identify the account by hash rather than by session alone. */
export function setAccountHash(hash: string): void {
	accountHash = hash;
}

export interface AttachmentRef {
	folder: string;
	uid: number;
	mimeIndex: string;
	mimeType: string;
	fileName: string;
}

/**
 * Builds a download or inline-view URL for an attachment.
 *
 * These are plain hrefs served by /?/Raw/, not JSON calls -- the browser fetches
 * them directly, which is what makes streaming a large attachment or previewing
 * a PDF work without pulling it through JavaScript first.
 */
export function attachmentUrl(
	attachment: AttachmentRef,
	mode: 'Download' | 'View' = 'Download'
): string {
	const key = encodeKey({
		folder: attachment.folder,
		uid: attachment.uid,
		mimeIndex: attachment.mimeIndex,
		mimeType: attachment.mimeType,
		fileName: attachment.fileName,
		accountHash
	});
	return `/?/Raw/&q[]=/0/${mode}/&q[]=/${key}`;
}
